/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from 'react';
import type {
  BaseProps,
  ComponentSize,
  ComponentVariant,
  ComponentAppearance,
} from 'src/modules/shared/types/component.type';
import { getDisabledClasses } from 'src/modules/shared/styles/component.styles';
import {
  tabsBaseClasses,
  tabsContainerClasses,
  tabsAppearanceClasses,
  tabsScrollFadeClasses,
  tabsScrollRowClasses,
} from 'src/modules/shared/components/atoms/tabs/tabs.styles';
import { useHorizontalOverflowIndicators } from 'src/modules/shared/hooks/use-horizontal-overflow-indicators';
import { getScrollLeftToReveal } from 'src/modules/shared/utils/scroll-reveal.util';
import { createContext, useContext, useEffect, useRef } from 'react';

export interface TabsProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  appearance?: ComponentAppearance;
  orientation?: 'horizontal' | 'vertical';
  allowWrap?: boolean;
  /** Keeps horizontal tabs on one row that scrolls sideways when it overflows. */
  scrollable?: boolean;
  align?: 'left' | 'center' | 'right';
}

/** Space kept between the selected tab and the faded edge of a scrolling row. */
const SCROLL_REVEAL_MARGIN_PX = 24;

const getScrollFadeClasses = (start: boolean, end: boolean): string => {
  if (start && end) return tabsScrollFadeClasses.both;
  if (start) return tabsScrollFadeClasses.start;
  return end ? tabsScrollFadeClasses.end : '';
};

/**
 * Tabs component for shared use across the app.
 *
 * Usage:
 * ```tsx
 * <Tabs value={activeTab} onChange={setActiveTab} align="center">
 *   <Tab value="general" label="General" />
 *   <Tab value="communications" label="Communications" />
 * </Tabs>
 * ```
 *
 * Keyboard navigation and ARIA supported.
 * Use `allowWrap={true}` to enable wrapping when space is constrained.
 * Use `scrollable={true}` to keep horizontal tabs on one swipeable row instead;
 * the selected tab scrolls into view and faded edges show hidden tabs.
 * Use `align="left" | "center" | "right"` to control text alignment within tabs.
 */
export const Tabs = ({
  value,
  onChange,
  children,
  className = '',
  variant = 'secondary',
  appearance = 'soft',
  size = 'md',
  disabled = false,
  orientation = 'horizontal',
  allowWrap = false,
  scrollable = false,
  align = 'center',
}: TabsProps) => {
  const isScrollable = scrollable && orientation === 'horizontal';
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showLeftOverflow, showRightOverflow } =
    useHorizontalOverflowIndicators({ ref: scrollRef, watchValue: value });

  useEffect(() => {
    const container = scrollRef.current;
    const activeTab = container?.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]'
    );
    if (!isScrollable || !container || !activeTab) return;

    const containerLeft = container.getBoundingClientRect().left;
    const tabRect = activeTab.getBoundingClientRect();
    container.scrollLeft = getScrollLeftToReveal({
      scrollLeft: container.scrollLeft,
      viewportWidth: container.clientWidth,
      itemLeft: tabRect.left - containerLeft + container.scrollLeft,
      itemWidth: tabRect.width,
      margin: SCROLL_REVEAL_MARGIN_PX,
    });
  }, [isScrollable, value]);

  const containerClasses = [
    ...tabsBaseClasses,
    tabsContainerClasses[size],
    tabsAppearanceClasses[appearance][variant],
    getDisabledClasses(disabled),
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const wrapClasses = allowWrap ? 'flex-wrap' : '';
  const horizontalLayoutClasses = isScrollable
    ? `${tabsScrollRowClasses} ${getScrollFadeClasses(showLeftOverflow, showRightOverflow)}`
    : `flex gap-2 ${wrapClasses}`;
  const tabsLayoutClasses =
    orientation === 'vertical'
      ? 'flex flex-col gap-2'
      : horizontalLayoutClasses;

  return (
    <div
      className={containerClasses}
      role="tablist"
      aria-orientation={orientation}
    >
      <TabsContext.Provider
        value={{
          value,
          onChange,
          disabled,
          variant,
          appearance,
          size,
          orientation,
          allowWrap,
          scrollable: isScrollable,
          align,
        }}
      >
        <div
          ref={isScrollable ? scrollRef : undefined}
          className={tabsLayoutClasses}
        >
          {children}
        </div>
      </TabsContext.Provider>
    </div>
  );
};

interface TabsContextValue {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  variant: ComponentVariant;
  appearance: ComponentAppearance;
  size: ComponentSize;
  orientation: 'horizontal' | 'vertical';
  allowWrap: boolean;
  scrollable: boolean;
  align: 'left' | 'center' | 'right';
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

export const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab must be used within Tabs');
  }
  return context;
};
